import type { ComponentDefinition, ComponentNode, ComponentSetting } from "./types";
import { CATALOG_SEED } from "./catalog-seed";

export const CATALOG: ComponentDefinition[] = CATALOG_SEED;

export const CATALOG_BY_ID: Record<string, ComponentDefinition> =
  Object.fromEntries(CATALOG.map((c) => [c.id, c]));

export function getDefinition(
  id: string,
  custom?: ComponentDefinition,
): ComponentDefinition | undefined {
  if (id === "custom" && custom) return custom;
  return Object.hasOwn(CATALOG_BY_ID, id) ? CATALOG_BY_ID[id] : undefined;
}

/** The node's own value, else the catalog default, else `undefined` when the
 *  definition doesn't declare the setting at all. */
export function getSetting(
  node: Pick<ComponentNode, "settings">,
  def: ComponentDefinition | undefined,
  settingId: string,
): boolean | undefined {
  const decl = def?.settings?.find((s) => s.id === settingId);
  if (!decl) return undefined;
  const v = node.settings?.[settingId];
  return v ?? decl.default;
}

/** Declared settings tagged `semantics === tag`, each with its resolved value. */
export function settingsBySemantic(
  node: Pick<ComponentNode, "settings">,
  def: ComponentDefinition | undefined,
  tag: NonNullable<ComponentSetting["semantics"]>,
): Array<{ setting: ComponentSetting; value: boolean }> {
  if (!def?.settings) return [];
  const out: Array<{ setting: ComponentSetting; value: boolean }> = [];
  for (const decl of def.settings) {
    if (decl.semantics !== tag) continue;
    const v = node.settings?.[decl.id] ?? decl.default;
    out.push({ setting: decl, value: v });
  }
  return out;
}

export type DefLookup = typeof getDefinition;

export function resolveImageFilename(
  def: ComponentDefinition,
): string | undefined {
  if (def.image) return def.image;
  if (def.category === "battery") {
    if (def.chemistry === "lifepo4") return "lifepo4-generic.webp";
    if (def.chemistry === "gel") return "gel-generic.webp";
    if (def.chemistry === "agm") return "agm-generic.webp";
    if (def.chemistry === "lead-acid") return "flooded-generic.webp";
    return "battery-generic.webp";
  }
  if (def.category === "charger" && def.pvMaxVoc != null) {
    return "mppt-generic.webp";
  }
  if (def.category === "engine") return "engine-generic.webp";
  if (def.category === "solar") return "solar-generic.webp";
  if (def.category === "load" && def.id.startsWith("load-fridge-")) {
    return "fridge-generic.webp";
  }
  return undefined;
}

export const CATEGORY_LABELS: Record<ComponentDefinition["category"], string> = {
  battery: "Batteries",
  charger: "Chargers & DC-DC",
  solar: "Solar",
  source: "Sources",
  engine: "Engines",
  distribution: "Distribution & Fuses",
  load: "Loads",
  monitor: "Monitoring",
};

export const CATEGORY_ORDER: ComponentDefinition["category"][] = [
  "battery",
  "engine",
  "source",
  "charger",
  "solar",
  "distribution",
  "monitor",
  "load",
];
